// @ts-nocheck
import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { X, Plus, Crown, Star } from 'lucide-react';
import { Database } from '@/integrations/supabase/types';

// Use the correct Membership type from Supabase schema
type SupabaseMembership = Database['public']['Tables']['memberships']['Row'];

interface ProfileMembership {
  id: string;
  membership_slug: string;
  is_active: boolean;
  assigned_at: string;
  expires_at?: string;
}

interface MultiMembershipSelectorProps {
  availableMemberships: SupabaseMembership[];
  currentMemberships: ProfileMembership[];
  selectedMemberships: string[];
  primaryMembership?: string | null;
  onAssignMembership: (membershipSlug: string) => Promise<boolean>;
  onRemoveMembership: (membershipSlug: string) => Promise<boolean>;
  onSetPrimary?: (membershipSlug: string) => void;
  isLoading?: boolean;
}

export const MultiMembershipSelector: React.FC<MultiMembershipSelectorProps> = ({
  availableMemberships,
  currentMemberships,
  selectedMemberships,
  primaryMembership,
  onAssignMembership,
  onRemoveMembership,
  onSetPrimary,
  isLoading = false
}) => {
  const assignedMembershipSlugs = currentMemberships.map(membership => membership.membership_slug);

  const getMembershipName = (slug: string) => {
    const membership = availableMemberships.find(m => m.slug === slug);
    return membership ? membership.name : slug;
  };

  const getMembershipBadgeColor = (membershipSlug: string) => {
    const membership = availableMemberships.find(m => m.slug === membershipSlug);
    if (membership?.is_premium) return 'bg-purple-100 text-purple-800';
    if (membership?.is_addon) return 'bg-blue-100 text-blue-800';
    switch (membershipSlug) {
      case 'press_premium': return 'bg-yellow-100 text-yellow-800';
      case 'press_pro': return 'bg-orange-100 text-orange-800';
      case 'premium': return 'bg-purple-100 text-purple-800';
      case 'pro': return 'bg-blue-100 text-blue-800';
      case 'staff': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleMembershipToggle = (membershipSlug: string, isChecked: boolean) => {
    if (isChecked) {
      onAssignMembership(membershipSlug);
    } else {
      onRemoveMembership(membershipSlug);
    }
  };

  // Remove unused function
  // Separate regular and addon memberships
  const regularMemberships = availableMemberships.filter(m => !m.is_addon);
  const addonMemberships = availableMemberships.filter(m => m.is_addon);

  const canRemoveMembership = (membershipSlug: string) => {
    // Don't allow removing athlete_free or other required base memberships
    const protectedMemberships = ['athlete_free', 'free'];
    return !protectedMemberships.includes(membershipSlug);
  };

  console.log('🎯 MultiMembershipSelector render:', {
    selectedMemberships,
    primaryMembership,
    availableMemberships: availableMemberships.map(m => ({ id: m.id, name: m.name, slug: m.slug })),
    onSetPrimary: !!onSetPrimary
  });

  return (
    <div className="space-y-4">
      {/* Current Memberships */}
      {currentMemberships.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">Current Memberships</Label>
          <div className="flex flex-wrap gap-2">
            {currentMemberships.map((membership) => (
              <Badge 
                key={membership.id}
                className={`${getMembershipBadgeColor(membership.membership_slug)} flex items-center gap-1`}
              >
                {getMembershipName(membership.membership_slug)}
                {canRemoveMembership(membership.membership_slug) && (
                  <button
                    onClick={() => onRemoveMembership(membership.membership_slug)}
                    disabled={isLoading}
                    className="ml-1 hover:bg-black/10 rounded-full p-0.5"
                  >
                    <X size={12} />
                  </button>
                )}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Regular Memberships */}
      {regularMemberships.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Crown className="h-4 w-4" />
              Regular Memberships
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {onSetPrimary ? (
              <RadioGroup 
                value={primaryMembership || ''} 
                onValueChange={(value) => {
                  console.log('📻 RadioGroup value changed:', value);
                  console.log('📻 Current primaryMembership:', primaryMembership);
                  if (onSetPrimary) {
                    console.log('📻 Calling onSetPrimary with:', value);
                    onSetPrimary(value);
                  } else {
                    console.log('📻 onSetPrimary is not available');
                  }
                }}
                className="space-y-2"
              >
                {regularMemberships.map((membership) => {
                  const isAssigned = assignedMembershipSlugs.includes(membership.slug);
                  const isSelected = selectedMemberships.includes(membership.slug);
                  
                  return (
                    <div key={membership.id} className="flex items-center space-x-3 p-2 rounded-md border">
                      <Checkbox
                        id={`membership-${membership.id}`}
                        checked={isSelected}
                        disabled={isLoading}
                        onCheckedChange={(checked) => 
                          handleMembershipToggle(membership.slug, checked as boolean)
                        }
                      />
                      <RadioGroupItem
                        value={membership.slug}
                        id={`primary-${membership.id}`}
                        disabled={!isSelected || isLoading}
                      />
                      <Label 
                        htmlFor={`primary-${membership.id}`}
                        className="flex-1 cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          {membership.name}
                          {membership.is_premium && <Star className="h-3 w-3 text-yellow-500" />}
                          {primaryMembership === membership.slug && (
                            <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                              <Crown className="h-3 w-3 mr-1" />
                              Primary
                            </Badge>
                          )}
                          {isAssigned && <span className="text-xs text-gray-500">(Current)</span>}
                        </div>
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            ) : (
              regularMemberships.map((membership) => {
                const isAssigned = assignedMembershipSlugs.includes(membership.slug);
                const isSelected = selectedMemberships.includes(membership.slug);
                
                return (
                  <div key={membership.id} className="flex items-center space-x-3 p-2 rounded-md border">
                    <Checkbox
                      id={`membership-${membership.id}`}
                      checked={isSelected}
                      disabled={isLoading}
                      onCheckedChange={(checked) => 
                        handleMembershipToggle(membership.slug, checked as boolean)
                      }
                    />
                    <Label 
                      htmlFor={`membership-${membership.id}`}
                      className="flex-1 cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        {membership.name}
                        {membership.is_premium && <Star className="h-3 w-3 text-yellow-500" />}
                        {isAssigned && <span className="text-xs text-gray-500">(Current)</span>}
                      </div>
                    </Label>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      )}

      {/* Addon Memberships */}
      {addonMemberships.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Addon Memberships
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {addonMemberships.map((membership) => {
              const isAssigned = assignedMembershipSlugs.includes(membership.slug);
              const isSelected = selectedMemberships.includes(membership.slug);
              
              return (
                <div key={membership.id} className="flex items-center space-x-3 p-2 rounded-md border">
                  <Checkbox
                    id={`addon-${membership.id}`}
                    checked={isSelected}
                    disabled={isLoading}
                    onCheckedChange={(checked) => 
                      handleMembershipToggle(membership.slug, checked as boolean)
                    }
                  />
                  <Label 
                    htmlFor={`addon-${membership.id}`}
                    className="flex-1 cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      {membership.name}
                      {membership.is_premium && <Star className="h-3 w-3 text-yellow-500" />}
                      {isAssigned && <span className="text-xs text-gray-500">(Current)</span>}
                    </div>
                  </Label>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* No longer need assign selected button since changes are tracked automatically */}
    </div>
  );
};
